import { createContext, createElement, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

import { buildFlagMap, buildSampleMap, buildSourceMap } from "./flagMap.mjs";
import type { BenchmarkSourceRow, FlagMap, FlagRow } from "./flagMap.d.mts";
import { getSupabase } from "./supabase";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * MER-576: put the insights engine's verdict on the tile.
 *
 * BENCHMARK + FLAG have been computed every refresh since v4.3 (68 KPIs in the
 * registry, ~171 benchmarks and ~49 RED flags for DWS) and synced to Supabase,
 * but the dashboard's only consumer was flagStream.ts, which counts RED rows to
 * raise a toast. KPITile has carried full GREEN/YELLOW/RED support -- colour
 * plus the mockup's check / warning / bang icons -- since MER-170, and MER-560
 * wired flagLevel to the owner-entered widget.target. No customer has set a
 * target, so in practice every value in the product rendered in
 * var(--text-primary) with no icon. That is the whole of the "palette is too
 * dark, needs colour across the board" report: the colour was already there and
 * nothing ever asked for it.
 *
 * ONE fetch per page, not per widget. Painting Overview already costs 46
 * requests and a full tab tour 243, with no caching layer anywhere, so a
 * per-tile flag query would have been the single worst way to add colour. The
 * provider fetches once and hands every widget a synchronous lookup.
 *
 * Resolution logic lives in flagMap.mjs so node:test can exercise it directly.
 * This file is deliberately NOT named flagMap.ts: a bare "../lib/flagMap"
 * import resolves to the .mjs under Rollup and to the .ts under tsc, so the
 * pair typechecked clean and failed the build. One name, one file.
 */

const EMPTY: FlagMap = new Map();

const FlagMapContext = createContext<FlagMap>(EMPTY);

const FLAG_COLUMNS =
  "benchmark_id,kpi_name,equipment_type,division,flag_level,benchmark_value,deviation_pct,period_start";

export async function fetchFlagMap(tenantId: string): Promise<FlagMap> {
  const supabase = getSupabase();
  // MER-716: the flag's benchmark source decides whether it may judge a tile
  // or only describe a trend. FLAG carries no source, so the benchmark rows
  // are read alongside it (one more request per page, in parallel).
  const [flags, benchmarks] = await Promise.all([
    scopeTenant(supabase.from("flag").select(FLAG_COLUMNS), tenantId),
    // MER-814: sample_size says how many periods survived the compute guards
    // and shaped the thresholds. Null on a manual target and on rows synced
    // before the column existed, which reads as unknown, not as thin.
    scopeTenant(
      supabase.from("benchmark").select("benchmark_id,benchmark_source,sample_size"),
      tenantId,
    ),
  ]);
  // Fail quiet. An unreadable flag table degrades to uncoloured tiles, never to
  // a broken page: the numbers are the product, the verdict is an overlay.
  if (flags.error || !Array.isArray(flags.data)) return EMPTY;
  // An unreadable benchmark table leaves every source unknown, which reads as
  // a trend: no source, no judgment.
  const readable = !benchmarks.error && Array.isArray(benchmarks.data);
  const benchmarkRows = readable ? (benchmarks.data as BenchmarkSourceRow[]) : [];
  const sources = readable ? buildSourceMap(benchmarkRows) : new Map<string, string>();
  const samples = readable ? buildSampleMap(benchmarkRows) : new Map<string, number>();
  return buildFlagMap(flags.data as FlagRow[], sources, samples);
}

export function FlagMapProvider(props: { tenantId: string | null; children: ReactNode }) {
  const { tenantId, children } = props;
  const [map, setMap] = useState<FlagMap>(EMPTY);

  useEffect(() => {
    if (!tenantId) {
      setMap(EMPTY);
      return;
    }
    let cancelled = false;
    fetchFlagMap(tenantId)
      .then((next) => {
        if (!cancelled) setMap(next);
      })
      .catch(() => {
        if (!cancelled) setMap(EMPTY);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return createElement(FlagMapContext.Provider, { value: map }, children);
}

export function useFlagMap(): FlagMap {
  return useContext(FlagMapContext);
}
